import React, { useContext, useEffect } from "react";
import { useHistory, useParams } from "react-router-dom";
import { useDispatch } from "react-redux";
import ProjectHeader from "../../_shared/ProjectHeader/ProjectHeader";
import { setDynamicBreadcrumbs } from "../../_shared/_redux/ProjectActions";
import { HeaderContext } from "../../../../HeaderContext";
import { breadCrumbsArray } from "../../../../data/breadCrumbs";
import { ANNUAL_OKR, ANNUAL_OKR_VIEW_OKRS } from "../../../../data/RouteUrls";
import useAnnualOkrStore, {
  useAnnualOkrStoreSelector,
} from "../_redux/AnnualOkrStore";
import OkrScorecardBoard, { ScorecardHeader } from "./OkrScorecardBoard";
import "./AnnualOkrViewOkrs.scss";

// ─────────────────────────────────────────────────────────────────────────────
// Company OKRs — the finished board, after the terminal lock (§7.5).
//
// Figma 34090-45674 — the coach's frame of the Scorecard. It is the CEO's board
// (34050-53370) with the status control inert, so `OkrScorecardBoard` is shared
// between the apps and no card is ever editable here: the progress PATCHes
// accept only the objective's owner and the CEO (AO-447), and a control the
// coach can move would 403 on change.
//
// Read via §6.9 `GET /coach/annual-okr/:uniqueId/tracking` — the coach's own
// tracking route, which returns the same shape as the CEO's §4.6 including
// `progress_status`, `owner_name` and each Key Result's `progress_percent`. No
// mapping; the payload goes straight to the board.
//
// ⚠️ Not the CEO's §4.6 (behind `UserAuthGuard`, needs a `company_id` the coach
// app can't send — it 500'd) and not §6.6 `okr-mode` (loads, but carries no
// progress or owner name, so every row read "Not Started"). Both were tried.
// See `AnnualOkrUrls.GET_TRACKING` for the full history.
// ─────────────────────────────────────────────────────────────────────────────

const selectClients = (state) => state.clients;
const selectTracking = (state) => ({
  tracking: state.tracking,
  trackingLoading: state.trackingLoading,
});

const AnnualOkrViewOkrs = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const { annualOkrId } = useParams();
  const HeaderMetaData = useContext(HeaderContext);

  const clients = useAnnualOkrStoreSelector(selectClients);
  const { tracking, trackingLoading } =
    useAnnualOkrStoreSelector(selectTracking);
  const client = (clients || []).find((c) => c.annualOkrId === annualOkrId);
  const companyName = client?.name || "";

  useEffect(() => {
    document.title = "Art of Scale | Annual OKR Scorecard";
    HeaderMetaData.setHeaderMetadata({ headerTitle: "Annual OKRs" });
    if (!useAnnualOkrStore.getState().clients) {
      useAnnualOkrStore.getState().fetchDashboard();
    }
    useAnnualOkrStore.getState().fetchTracking(annualOkrId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    dispatch(
      setDynamicBreadcrumbs(ANNUAL_OKR_VIEW_OKRS, [
        breadCrumbsArray[ANNUAL_OKR][0],
        { title: companyName || "Annual OKR Scorecard", url: "" },
      ]),
    );
  }, [dispatch, companyName]);

  return (
    <div>
      <ProjectHeader
        handleNavigation={() => history.push(ANNUAL_OKR)}
        showBack
      />
      <div id="annual-okr-view-okrs">
        <div className="okrs-page">
          {/* Redesign 2026-10-09: the same header the CEO sees, from the
              shared board file. */}
          <ScorecardHeader tracking={tracking} />
          {/* Read-only: no `canEditObjective`, so no card is editable, and no
              `explainLocked`/`footnote` either — those are for the company's
              own members in the CEO app (AO-447/AO-448). */}
          <OkrScorecardBoard
            tracking={tracking}
            loading={trackingLoading && !tracking}
          />
        </div>
      </div>
    </div>
  );
};

export default AnnualOkrViewOkrs;
